'use client';

import * as React from 'react';
import { useClerk, useOrganization, useUser } from '@clerk/clerk-react';
import { resetAnalytics } from '@tagadapay/analytics';
import { LogOut, ArrowLeft } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';

import { BrandLogo } from '@/components/branding/brand-logo';
import { usePlatformBranding } from '@/hooks/use-platform-branding';
import { useBrandTheme } from '@/hooks/use-brand-theme';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ThemeToggle } from '@/components/ui/theme-toggle';

interface AdminAppLayoutProps {
  children: React.ReactNode;
}

/**
 * Thin chrome for admin/partner-admin pages.
 *
 * Unlike SidebarLayout, this does NOT depend on an active Clerk org —
 * admin sections are gated by global role (superadmin/partneradmin)
 * and present cross-org data, so showing the org switcher / store
 * list / processor nav would be misleading.
 */
export function AdminAppLayout({ children }: AdminAppLayoutProps) {
  const brand = usePlatformBranding();
  useBrandTheme(brand);
  const { signOut } = useClerk();
  const { user } = useUser();
  const { organization } = useOrganization();
  const navigate = useNavigate();

  const handleSignOut = async () => {
    resetAnalytics();
    await signOut();
    navigate('/sign-in');
  };

  // Link back to whatever org the user last had active. If none,
  // OrgRedirect at "/" will pick their first membership.
  const backToAppHref = organization?.slug ? `/org/${organization.slug}` : '/';

  const userInitials = (user?.firstName?.[0] ?? '') + (user?.lastName?.[0] ?? '');

  return (
    <div className="bg-background flex h-screen min-h-0 flex-1 flex-col">
      <header className="border-border bg-background/80 z-30 flex h-14 shrink-0 items-center gap-4 border-b px-4 backdrop-blur sm:px-6">
        <Link to={backToAppHref} className="flex items-center gap-2">
          <BrandLogo brand={brand} className="h-7 w-auto" />
          <span className="text-sm font-semibold tracking-tight">{brand.appName}</span>
        </Link>

        <div className="ml-auto flex items-center gap-2">
          <Button variant="ghost" size="sm" asChild>
            <Link to={backToAppHref}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to dashboard
            </Link>
          </Button>
          <ThemeToggle />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="rounded-full">
                <Avatar className="h-8 w-8">
                  <AvatarImage src={user?.imageUrl} alt={user?.fullName ?? ''} />
                  <AvatarFallback>{userInitials || 'U'}</AvatarFallback>
                </Avatar>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuItem asChild>
                <Link to={backToAppHref}>
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  <span className="text-sm">Back to dashboard</span>
                </Link>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleSignOut}>
                <LogOut className="mr-2 h-4 w-4" />
                <span className="text-sm">Sign out</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      <main className="bg-background min-h-0 w-full flex-1 overflow-auto px-4 py-6 sm:px-6">
        {children}
      </main>
    </div>
  );
}
